import * as api from "./client.js";

/**
 * How a lawyer entry was (or was not) linked to an Address Book contact
 * (DEV-2662). `manual` with a null
 * `contactId` means staff confirmed there is no matching contact.
 */
export type ContactMatchStatus =
  | "pending"
  | "auto_matched"
  | "ambiguous"
  | "unmatched"
  | "manual"
  | "not_applicable";

/** A suggestion stored by the matcher, with the evidence for why it was suggested. */
export type ContactMatchCandidate = {
  contactId: string;
  name: string;
  jobTitle: string | null;
  officeCountry: string | null;
  activeAtFirm: boolean;
  countryMatch: boolean;
  /** Null when data-marts could not be asked at match time. */
  rankedInCategory: boolean | null;
};

export type SubmissionEntry = {
  id: string;
  personName: string | null;
  submissionId: string;
  createdAt: string;
  createdBy: string | null;
  contactId: string | null;
  contactName: string | null;
  contactMatchStatus: ContactMatchStatus;
  contactMatchCandidates: ContactMatchCandidate[] | null;
};

export type UpdateEntryContactResponse = {
  entry: SubmissionEntry;
  /** Soft warning: the contact has no active role at the submission's firm. */
  warning?: "contact_not_at_firm";
  requestId?: string;
};

export type RematchEntryContactResponse = {
  entry: SubmissionEntry;
  requestId?: string;
};

export type EntryContactHistoryAction =
  | "link"
  | "relink"
  | "unlink"
  | "no_contact"
  | "rematch"
  | "auto_match"
  | "firm_selected";

export type EntryContactHistoryItem = {
  id: string;
  action: EntryContactHistoryAction;
  previousContactId: string | null;
  previousContactName: string | null;
  contactId: string | null;
  contactName: string | null;
  previousStatus: string | null;
  status: string;
  method: string | null;
  actorType: "user" | "system";
  actorId: string | null;
  actorEmail: string | null;
  createdAt: string;
};

export type EntryContactHistoryResponse = {
  history: EntryContactHistoryItem[];
  requestId?: string;
};

/**
 * Address Book contacts ranked for the submission's firm in its practice area, as
 * resolved server-side by submission-api (staff.submission.read). `available: false`
 * means the ranking data couldn't be asked (e.g. data-marts down, no practice area).
 */
export type SubmissionRankedContacts = {
  available: boolean;
  contactIds: string[];
  requestId?: string;
};

export type Submission = {
  submissionId?: string;
  firmName: string;
  firmRef?: string;
  practiceArea?: string;
  submissionDocx?: "Y" | "N";
  referee?: number;
  practiceAreaId?: number | null;
  practiceAreaName?: string;
  practiceGroupId?: number | null;
  practiceGroupName?: string | null;
  regionId?: number | null;
  regionName?: string;
  tableId?: number | null;
  tableName?: string | null;
  publicationName?: string;
  edition?: string;
  createdAt?: string;
  submissionType?: string | null;
  createdBy?: string;
  publicationId?: number | null;
  editorialCategoryId?: number | null;
  refereeType?: "firm" | "bar" | "lawyer" | "mediator";
  closeDate?: string | null;
  totalReferees?: number;
  totalPeople?: number;
  responseCount?: number;
  personName?: string | null;
  fileUri?: string | null;
  subtype?: string | null;
  editionId?: string | null;
  rankingEntityId?: string | null;
  bpaId?: string | null;
  editionBpaId?: string | null;
  rankingTableId?: string | null;
  submissionEntryId?: string;
  submissionEntryFileId?: string | null;
  /** Address Book office uuid the submission was made for, when known. */
  officeId?: string | null;
  contactId?: string | null;
  contactName?: string | null;
  contactMatchStatus?: ContactMatchStatus;
  contactMatchCandidates?: ContactMatchCandidate[] | null;
};

export type TaxonomyMode = "legacy" | "new";

export type SubmissionsFilters = {
  limit?: number;
  offset?: number;
  publicationId?: number;
  regionId?: number;
  edition?: string;
  practiceAreaId?: number;
  editionId?: string;
  rankingEntityId?: string;
  bpaId?: string;
  taxonomy?: TaxonomyMode;
  search?: string;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  /** Sent as a comma list; empty or undefined means no status filter. */
  contactMatchStatus?: ContactMatchStatus[];
  /**
   * Only submissions whose account is approved (`firm_ref IS NOT NULL`). submission-api
   * already defaults this to true, so only `true` is ever sent — its query schema uses
   * `z.coerce.boolean()`, which would read the string "false" as true anyway.
   */
  verifiedFirmOnly?: boolean;
};

export type SubmissionsResponse = {
  submissions: Submission[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  requestId?: string;
};

export type SubmissionUniqueValue = {
  id: string | number | null;
  name: string;
  practiceGroupName?: string | null;
  editionId?: string | null;
  edition?: string | null;
  rankingEntityId?: string | null;
  bpaId?: string | null;
};

export type SubmissionUniquesResponse = {
  values: SubmissionUniqueValue[];
  requestId?: string;
};

export type SubmissionUniquesFilters = {
  edition?: string;
  publicationId?: number;
  regionId?: number;
  editionId?: string;
  rankingEntityId?: string;
  bpaId?: string;
  taxonomy?: TaxonomyMode;
};

// Whole-submission summary shown before a CS force-delete (SOFTSUPP-312 Case 2).
// activeRefereeCount is aggregated across every entry (soft-deleted referees excluded)
// so it does not suffer the per-entry / pagination limits of the list row.
export type SubmissionDeleteSummary = {
  activeRefereeCount: number;
  entries: Array<{ personName: string | null }>;
};

export const submissionsService = {
  async get(filters?: SubmissionsFilters): Promise<SubmissionsResponse> {
    const queryParams = new URLSearchParams();

    if (filters?.limit !== undefined) {
      queryParams.append("limit", String(filters.limit));
    }
    if (filters?.offset !== undefined) {
      queryParams.append("offset", String(filters.offset));
    }
    if (filters?.publicationId !== undefined) {
      queryParams.append("publicationId", String(filters.publicationId));
    }
    if (filters?.regionId !== undefined) {
      queryParams.append("regionId", String(filters.regionId));
    }
    if (filters?.edition !== undefined) {
      queryParams.append("edition", String(filters.edition));
    }
    if (filters?.practiceAreaId !== undefined) {
      queryParams.append("practiceAreaId", String(filters.practiceAreaId));
    }
    if (filters?.editionId !== undefined) {
      queryParams.append("editionId", filters.editionId);
    }
    if (filters?.rankingEntityId !== undefined) {
      queryParams.append("rankingEntityId", filters.rankingEntityId);
    }
    if (filters?.bpaId !== undefined) {
      queryParams.append("bpaId", filters.bpaId);
    }
    if (filters?.search !== undefined && filters.search !== "") {
      queryParams.append("search", filters.search);
    }
    if (filters?.sortField !== undefined) {
      queryParams.append("sortField", filters.sortField);
    }
    if (filters?.sortDirection !== undefined) {
      queryParams.append("sortDirection", filters.sortDirection);
    }
    if (filters?.contactMatchStatus !== undefined && filters.contactMatchStatus.length > 0) {
      queryParams.append("contactMatchStatus", filters.contactMatchStatus.join(","));
    }
    if (filters?.verifiedFirmOnly === true) {
      queryParams.append("verifiedFirmOnly", "true");
    }
    if (filters?.taxonomy !== undefined) {
      queryParams.append("taxonomy", filters.taxonomy);
    }

    const queryString = queryParams.toString();
    const endpoint = `/v1/submission/submissions/all${queryString ? `?${queryString}` : ""}`;

    const response = await api.get<SubmissionsResponse>(endpoint);

    const mappedSubmissions = response.submissions.map(submission => ({
      ...submission,
      submissionDocx: (submission.fileUri ? "Y" : "N") as "Y" | "N",
      referee: submission.totalReferees || 0,
      practiceArea: submission.practiceAreaName || "",
    }));

    return {
      ...response,
      submissions: mappedSubmissions,
    };
  },

  async getUniques(
    field: string,
    filters?: SubmissionUniquesFilters,
  ): Promise<SubmissionUniquesResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append("field", field);

    if (filters?.edition !== undefined) {
      queryParams.append("edition", filters.edition);
    }
    if (filters?.publicationId !== undefined) {
      queryParams.append("publicationId", String(filters.publicationId));
    }
    if (filters?.regionId !== undefined) {
      queryParams.append("regionId", String(filters.regionId));
    }
    if (filters?.editionId !== undefined) {
      queryParams.append("editionId", filters.editionId);
    }
    if (filters?.rankingEntityId !== undefined) {
      queryParams.append("rankingEntityId", filters.rankingEntityId);
    }
    if (filters?.bpaId !== undefined) {
      queryParams.append("bpaId", filters.bpaId);
    }
    if (filters?.taxonomy !== undefined) {
      queryParams.append("taxonomy", filters.taxonomy);
    }

    const endpoint = `/v1/submission/submissions/all/uniques?${queryParams.toString()}`;
    return api.get<SubmissionUniquesResponse>(endpoint);
  },

  // CS-only force delete (SOFTSUPP-312 Case 2). Hits staff-bff, which auto-proxies
  // to submission-api's DELETE /api/v1/submissions/:id/force (perm admin.submission.delete).
  // Deletes the submission even when it has uploaded documents / referees.
  async forceDelete(id: string): Promise<void> {
    await api.del(`/v1/submission/submissions/${id}/force`);
  },

  // Fetches the whole-submission delete summary (active referee count + all entries)
  // so the confirmation modal can show accurate cross-entry totals.
  /**
   * Link, relink or clear the Address Book contact of a lawyer entry. `null` records
   * "no matching contact" (status `manual`, no contact). Needs staff.submission.write.
   * A contact with no active role at the firm is still linked, with a `warning`.
   */
  async updateEntryContact(
    submissionId: string,
    entryId: string,
    contactId: string | null,
  ): Promise<UpdateEntryContactResponse> {
    return api.patch<UpdateEntryContactResponse>(
      `/v1/submission/submissions/${encodeURIComponent(submissionId)}/entries/${encodeURIComponent(entryId)}/contact`,
      { contactId },
    );
  },

  /** Re-runs the auto-matcher for one entry, overriding a manual link. */
  async rematchEntryContact(
    submissionId: string,
    entryId: string,
  ): Promise<RematchEntryContactResponse> {
    return api.post<RematchEntryContactResponse>(
      `/v1/submission/submissions/${encodeURIComponent(submissionId)}/entries/${encodeURIComponent(entryId)}/contact/rematch`,
      {},
    );
  },

  async getEntryContactHistory(
    submissionId: string,
    entryId: string,
  ): Promise<EntryContactHistoryResponse> {
    return api.get<EntryContactHistoryResponse>(
      `/v1/submission/submissions/${encodeURIComponent(submissionId)}/entries/${encodeURIComponent(entryId)}/contact/history`,
    );
  },

  async getRankedContacts(submissionId: string): Promise<SubmissionRankedContacts> {
    return api.get<SubmissionRankedContacts>(
      `/v1/submission/submissions/${encodeURIComponent(submissionId)}/ranked-contacts`,
    );
  },

  async getDeleteSummary(id: string): Promise<SubmissionDeleteSummary> {
    return api.get<SubmissionDeleteSummary>(`/v1/submission/submissions/${id}/delete-summary`);
  },
};
